/**
 * ProductKindSwitch (BLU-1373, D-13): the per-product Custom / Ready-made
 * switch for a product whose Shopify product type is Mixed.
 *
 * Renders only when the SAVED settings make the product's type Mixed
 * (`productTypeKind`, which reads Mixed only when the client sells both). A
 * product of a Mixed type is Ready-made until it is switched to Custom (the
 * Mixed default): its value is `isCustomSyncProduct` over the saved settings,
 * the same rule the server plans from.
 *
 * Admins flip it through a SegmentedControl, saving the product id into
 * `customProductIds` through the buffer's `savePatch` (the existing settings
 * mutation). Anyone else reads the value as text. The control refuses:
 *   - while the buffer holds other unsaved edits, because `savePatch` would
 *     carry them along (a visible note says so);
 *   - while a save or a settings refetch is in flight, so two quick flips
 *     never build on a stale list (the buffer writes the saved doc into the
 *     cache before it refetches; this closes the rest of the window);
 *   - at the 500 product cap, for a product not already listed (a plain note,
 *     never the raw validation error). A listed product can always switch
 *     back to Ready-made.
 *
 * A product that already has synced variants, or whose sync status is
 * unknown, asks first: its SKUs change the next time it syncs and orders
 * placed before then keep the old SKUs. Nothing re-syncs on its own. The
 * confirm is reversible, so its action is an orange outline (DESIGN.md
 * §13.6, mirrors `SupplySavedSummary`).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import {
  isCustomSyncProduct,
  MAX_CUSTOM_PRODUCT_IDS,
  productTypeKind,
  withCustomProductId,
} from '@threadheads/shared';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { NoteLine } from '@/components/shared/NoteLine';
import type { PreviewProduct } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

type KindValue = 'readyMade' | 'custom';

export interface ProductKindSwitchProps {
  product: Pick<PreviewProduct, 'id' | 'title' | 'productType' | 'syncedVariantCount'>;
  buffer: ProductSyncSettingsBuffer;
  admin: boolean;
  /** The row's projection lookup failed, so `syncedVariantCount` is unknown: treat it as synced. */
  syncStatusUnavailable: boolean;
}

export function ProductKindSwitch({ product, buffer, admin, syncStatusUnavailable }: ProductKindSwitchProps) {
  const { t } = useTranslation('productSync');
  const [confirmTarget, setConfirmTarget] = useState<KindValue | null>(null);
  const groupRef = useRef<HTMLSpanElement>(null);

  const settings = buffer.serverSettings;
  const typeName = product.productType?.name ?? '';
  if (productTypeKind(settings, typeName) !== 'mixed') return null;

  const value: KindValue = isCustomSyncProduct(settings, typeName, product.id) ? 'custom' : 'readyMade';
  const storedIds = settings.customProductIds ?? [];
  const atCap = storedIds.length >= MAX_CUSTOM_PRODUCT_IDS && !storedIds.includes(product.id);
  const disabled = buffer.hasChanges || buffer.isSaving || buffer.isFetching || atCap;
  const needsConfirm = syncStatusUnavailable || product.syncedVariantCount > 0;

  // Built from the saved list at the moment of saving, so a flip that just
  // landed is kept.
  const save = (next: KindValue) =>
    buffer.savePatch({
      customProductIds: withCustomProductId(buffer.serverSettings.customProductIds ?? [], product.id, next === 'custom'),
    });

  const choose = (next: KindValue) => {
    if (next === value || disabled) return;
    if (needsConfirm) {
      setConfirmTarget(next);
      return;
    }
    save(next);
  };

  const label = (kind: KindValue) => t(kind === 'custom' ? 'kind.custom' : 'kind.readyMade');

  return (
    <div className="flex flex-col gap-1.5" data-testid="product-kind-switch">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-medium text-foreground">{t('kind.label')}</span>
        {admin ? (
          <span ref={groupRef}>
            <SegmentedControl<KindValue>
              options={[
                { value: 'readyMade', label: label('readyMade') },
                { value: 'custom', label: label('custom') },
              ]}
              value={value}
              onChange={choose}
              ariaLabel={t('kind.groupAria', { name: product.title })}
              disabled={disabled}
            />
          </span>
        ) : (
          <span className="font-semibold text-foreground">{label(value)}</span>
        )}
        <span className="min-w-0 break-words text-muted-foreground">{t('kind.hint')}</span>
      </div>
      {admin && buffer.hasChanges && <NoteLine icon={Info}>{t('kind.dirtyNote')}</NoteLine>}
      {admin && atCap && (
        <NoteLine icon={Info} tone="warning">
          {t('kind.limitNote', { max: MAX_CUSTOM_PRODUCT_IDS })}
        </NoteLine>
      )}
      {value === 'custom' && <NoteLine>{t('kind.customNote')}</NoteLine>}

      <AlertDialog open={confirmTarget !== null} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent
          className="motion-reduce:animate-none"
          onCloseAutoFocus={(event) => {
            const pressed = groupRef.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]');
            if (pressed && !pressed.disabled) {
              event.preventDefault();
              pressed.focus();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="[overflow-wrap:anywhere]">{t('kind.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="leading-normal [overflow-wrap:anywhere]">
              {t('kind.confirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('kind.cancel')}</AlertDialogCancel>
            {/* Reversible: orange outline, never red (DESIGN.md §13.6). */}
            <Button
              type="button"
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
              onClick={() => {
                const target = confirmTarget;
                setConfirmTarget(null);
                if (target) save(target);
              }}
            >
              {t('kind.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
